/**
 * Which objects get movement handles in Synth VR (docs/spec/srvr/gizmos.md SRVR-0335).
 *
 * Pure arithmetic with no React or store imports of its own, so it is unit-tested under jsdom; the scene
 * decides *when* to ask (only while paused and with Shift held) and what to draw.
 */
import { effectiveAreaRange } from '../../store/areaSelection';
import type { AreaGesture, AreaRange } from '../../store/areaSelection';
import { onBeatTolerance } from './halo';

/** With no area selected, handles reach this many whole beats forward from the editor plane. */
export const GIZMO_AHEAD_BEATS = 2;

/** The most handle sets drawn at once in Synth VR, so a very long area on a dense chart cannot flood the
 *  scene with arrow meshes. Past it the nearest objects keep theirs. */
export const GIZMO_MAX_OBJECTS = 200;

/** The slice of editor state the window depends on. */
export interface GizmoWindowState {
  activeView?: string;
  areaRange: AreaRange | null;
  areaGesture: AreaGesture | null;
  playback: { currentBeat: number };
  scrollStep: number;
  beatmap?: { bpm?: number } | null;
}

/** The beats, inclusive, whose objects carry handles. `from` can exceed `to`: then none do. */
export interface GizmoWindow { from: number; to: number }

/**
 * Handles never sit behind the editor plane (an object before the playhead beat, give or take the
 * on-the-beat tolerance, has none). Inside that: every object in the selected area when there is one,
 * otherwise every object up to two whole beats ahead of the plane.
 *
 * `playheadBeat` is the chart beat the editor plane stands at, in the same frame as an object's
 * `time_beats`.
 */
export function gizmoWindow(s: GizmoWindowState, playheadBeat: number): GizmoWindow {
  const tol = onBeatTolerance(s.activeView, s.scrollStep, s.beatmap?.bpm);
  const area = effectiveAreaRange(s.areaRange, s.areaGesture, s.playback.currentBeat);
  const planeFrom = playheadBeat - tol;
  if (area) return { from: Math.max(area.startBeat, planeFrom), to: area.endBeat };
  return { from: planeFrom, to: playheadBeat + GIZMO_AHEAD_BEATS };
}

/** Whether an object at `beat` carries handles under `w`. */
export function inGizmoWindow(w: GizmoWindow, beat: number): boolean {
  return beat >= w.from && beat <= w.to + 1e-9;
}
